import { supabase } from '../lib/supabase'
import { ClosingStockJournal } from '../types'

/**
 * The closing-stock journal (§6.2 iii).
 *
 * This was a calculation the browser did while a tab was open: DR 631 / CR 355
 * for the sum of landed costs, with the reversal date printed underneath as a
 * sentence. Nothing was recorded, so there was no way to know whether a month
 * had been posted, no reversal ever happened, and "pushed to Xero" was a
 * heading above a number.
 *
 * A journal is now a row with a state — draft, posted, reversed — one per
 * period. That is what makes the two things the spec asks for possible: an
 * auto-reversal on the first of the following month, and a traceable push.
 */

export interface JournalDraft {
  periodMonth: string // YYYY-MM-01
  amount: number
  vehicleCount: number
  reverseOn: string // YYYY-MM-DD
}

export async function getJournal(periodMonth: string): Promise<ClosingStockJournal | null> {
  const { data, error } = await supabase
    .from('closing_stock_journals')
    .select('*')
    .eq('period_month', periodMonth)
    .maybeSingle()
  if (error) throw error
  return (data as ClosingStockJournal) || null
}

export async function listJournals(limit = 12): Promise<ClosingStockJournal[]> {
  const { data, error } = await supabase
    .from('closing_stock_journals')
    .select('*')
    .order('period_month', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data || []) as ClosingStockJournal[]
}

/**
 * Post the journal for a period.
 *
 * Refuses to post twice. A month's closing stock is a single fact, and posting
 * it again would double the entry in Xero — the unique index on period_month
 * enforces the same thing at the database, but failing here gives a person a
 * sentence rather than a constraint violation.
 */
export async function postJournal(
  draft: JournalDraft,
  userId?: string
): Promise<{ ok: boolean; message: string; journal?: ClosingStockJournal }> {
  const existing = await getJournal(draft.periodMonth)
  if (existing && existing.status !== 'draft') {
    return {
      ok: false,
      message: `${draft.periodMonth.slice(0, 7)} was already posted${
        existing.pushed_at ? ' and pushed to Xero' : ''
      }. Reverse it before posting again.`,
    }
  }

  const payload = {
    period_month: draft.periodMonth,
    debit_account: '631',
    credit_account: '355',
    amount: Math.round(draft.amount * 100) / 100,
    vehicle_count: draft.vehicleCount,
    bas_excluded: true,
    status: 'posted' as const,
    reverse_on: draft.reverseOn,
    created_by: userId || null,
  }

  const { data, error } = existing
    ? await supabase
        .from('closing_stock_journals')
        .update(payload)
        .eq('id', existing.id)
        .select()
        .single()
    : await supabase.from('closing_stock_journals').insert([payload]).select().single()

  if (error) throw error

  return {
    ok: true,
    message: `Closing stock for ${draft.periodMonth.slice(0, 7)} posted — DR 631 / CR 355, $${payload.amount.toLocaleString('en-AU')}, BAS excluded. Reverses ${draft.reverseOn}.`,
    journal: data as ClosingStockJournal,
  }
}

/**
 * Reverse a posted journal.
 *
 * §6.2 has this happening automatically on the 1st. There is no scheduler in
 * this platform yet, so `reverseDueJournals` is called when the reports screen
 * loads: any posted journal whose reverse date has passed is reversed then.
 * That is honest about what it is — a catch-up, not a cron — and it means the
 * ledger is never left showing an entry that should have been backed out weeks
 * ago just because nobody pressed a button.
 */
export async function reverseJournal(
  id: string
): Promise<{ ok: boolean; message: string }> {
  const { error } = await supabase
    .from('closing_stock_journals')
    .update({ status: 'reversed', reversed_at: new Date().toISOString() })
    .eq('id', id)
    .eq('status', 'posted')
  if (error) throw error
  return { ok: true, message: 'Journal reversed — DR 355 / CR 631.' }
}

export async function reverseDueJournals(): Promise<number> {
  const today = new Date().toISOString().slice(0, 10)
  const { data, error } = await supabase
    .from('closing_stock_journals')
    .select('id')
    .eq('status', 'posted')
    .lte('reverse_on', today)
  if (error) throw error

  for (const j of data || []) {
    await reverseJournal((j as any).id)
  }
  return (data || []).length
}

/**
 * Push a posted journal to Xero.
 *
 * The endpoint is a provider adapter in the same shape as the image and SMS
 * work: it returns 501 until Xero credentials are configured, rather than
 * pretending. `pushed_at` and `xero_journal_id` are stamped only once the
 * response actually carries an id — a 200 with an error body must not be
 * recorded as a successful push.
 */
export async function pushJournalToXero(
  journal: ClosingStockJournal
): Promise<{ ok: boolean; message: string }> {
  let res: Response
  try {
    res = await fetch('/api/xero/manual-journal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        narration: `Closing stock ${journal.period_month.slice(0, 7)}`,
        date: journal.period_month,
        basExcluded: journal.bas_excluded,
        lines: [
          { accountCode: journal.debit_account, lineAmount: journal.amount, description: 'Vehicle inventory' },
          { accountCode: journal.credit_account, lineAmount: -journal.amount, description: 'Cost of goods sold' },
        ],
      }),
    })
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Could not reach the Xero endpoint.' }
  }

  const type = res.headers.get('content-type') || ''
  if (!type.includes('application/json')) {
    return { ok: false, message: 'The Xero endpoint is not deployed on this environment.' }
  }

  const body = await res.json().catch(() => ({} as any))
  if (!res.ok || !body?.success || !body?.journalId) {
    return { ok: false, message: body?.error || `Xero rejected the journal (${res.status}).` }
  }

  await supabase
    .from('closing_stock_journals')
    .update({ xero_journal_id: body.journalId, pushed_at: new Date().toISOString() })
    .eq('id', journal.id)

  return { ok: true, message: `Posted to Xero as ${body.journalId}.` }
}
